import React, { useState, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// PDF.js worker setup
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const PDFViewer = ({ pdfUrl, pages }) => {
    const [pageSizes, setPageSizes] = useState({});
    const [zoom, setZoom] = useState(1.5); // default zoom level
    const [showHighlights, setShowHighlights] = useState(true);
    const [numPages, setNumPages] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    const pageRefs = useRef({});
    const scrollContainerRef = useRef(null);
    const isProgrammaticScroll = useRef(false);

    const handleRenderSuccess = (pageNumber, width, height) => {
        setPageSizes((prev) => ({
            ...prev,
            [pageNumber]: { width, height },
        }));
    };

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    const scrollToPage = (pageNum) => {
        if (pageRefs.current[pageNum]) {
            isProgrammaticScroll.current = true;
            // pageRefs.current[pageNum].scrollIntoView({
            //   behavior: "smooth",
            //   block: "start",
            // });
            const element = pageRefs.current[pageNum];
            const container = scrollContainerRef.current;

            // Use a more direct approach similar to form submission
            const scrollPosition = element.offsetTop - container.offsetTop;
            container.scrollTop = scrollPosition;

            setCurrentPage(pageNum);
            // Give time for scroll to settle
            setTimeout(() => {
                isProgrammaticScroll.current = false;
            }, 500); // adjust timing if needed
        }
    };

    return (
        <div ref={scrollContainerRef} className="relative w-full h-full overflow-auto">
            {/* tool bar */}
            <div className="sticky top-4 left-0 z-50 w-fit mx-auto bg-white/95 backdrop-blur-sm shadow-lg rounded-full border border-gray-200 px-6 py-2 flex items-center justify-center gap-4 select-none">
                {/* Page navigation */}
                <div className="flex items-center gap-2">
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const newPage = Math.max(1, currentPage - 1);
                            if (newPage !== currentPage) {
                                scrollToPage(newPage);
                            }
                        }}
                        disabled={currentPage <= 1}
                        className={`p-2 rounded-full transition ${currentPage <= 1
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-gray-100 hover:bg-gray-200 text-gray-800"
                            }`}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                    </button>

                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            const page = parseInt(e.target.page.value, 10);
                            if (page >= 1 && page <= numPages) {
                                scrollToPage(page);
                            } else {
                                // Reset to current page if invalid
                                e.target.page.value = currentPage;
                            }
                        }}
                        className="flex items-center gap-1"
                    >
                        <input
                            name="page"
                            type="number"
                            min="1"
                            max={numPages}
                            defaultValue={currentPage}
                            key={currentPage}
                            className="w-12 px-2 py-1 text-sm border border-gray-200 rounded-full text-center focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            onBlur={(e) => {
                                const page = parseInt(e.target.value, 10);
                                if (isNaN(page) || page < 1 || page > numPages) {
                                    e.target.value = currentPage;
                                }
                            }}
                        />
                        <span className="text-sm text-gray-600">of {numPages}</span>
                    </form>

                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const newPage = Math.min(numPages, currentPage + 1);
                            if (newPage !== currentPage) {
                                scrollToPage(newPage);
                            }
                        }}
                        disabled={currentPage >= numPages}
                        className={`p-2 rounded-full transition ${currentPage >= numPages
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-gray-100 hover:bg-gray-200 text-gray-800"
                            }`}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>

                {/* Divider */}
                <div className="h-6 w-px bg-gray-200" />

                {/* Zoom controls */}
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setZoom((prev) => Math.max(0.5, prev - 0.1))}
                        className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                    </button>
                    <button
                        onClick={() => setZoom(1.5)}
                        disabled={zoom === 1.5}
                        className={`text-sm font-medium transition ${zoom === 1.5
                            ? "text-gray-700 cursor-default"
                            : "text-indigo-600 hover:text-indigo-700"
                            }`}
                    >
                        {(zoom * 100).toFixed(0)}%
                    </button>
                    <button
                        onClick={() => setZoom((prev) => Math.min(3, prev + 0.1))}
                        className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            </div>

            <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                className="flex flex-col items-center"
            >
                {pages.map(({ pageNumber, fields, originalWidth, originalHeight }) => {

                    return (
                        <div
                            key={pageNumber}
                            ref={(el) => {
                                if (el) pageRefs.current[pageNumber] = el;
                            }}
                            className="relative mb-5 pointer-events-none"
                        >
                            <Page
                                scale={zoom}
                                pageNumber={pageNumber}
                                onRenderSuccess={({ width, height }) =>
                                    handleRenderSuccess(pageNumber, width, height)
                                }
                            />
                        </div>
                    );
                })}
            </Document>
        </div>
    );
};

export default PDFViewer;
